import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Server, Monitor, Activity, MapPin, AlertTriangle, FileBarChart2, ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";

const today = new Date().toISOString().slice(0, 10);
const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

export default function GatewayList() {
  const { t } = useTranslation();
  const [gateways, setGateways] = useState([]);
  const [gatewayPenalties, setGatewayPenalties] = useState({});
  const [penaltyFilter, setPenaltyFilter] = useState("all");

  const [reportOpen, setReportOpen] = useState(false);
  const [reportRange, setReportRange] = useState("monthly");
  const [reportAppliedRange, setReportAppliedRange] = useState(null);
  const [reportStartDate, setReportStartDate] = useState(thirtyDaysAgo);
  const [reportEndDate, setReportEndDate] = useState(today);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [showAllPeriods, setShowAllPeriods] = useState(false);

  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    async function fetchGateways() {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_BASE}/gateways`, {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();
        setGateways(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching gateways:", err);
      }
    }

    fetchGateways();
    const interval = setInterval(fetchGateways, 30000);

    // Refresh immediately when the tab becomes visible again
    function handleVisibilityChange() {
      if (document.visibilityState === "visible") fetchGateways();
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [API_BASE]);

  // Poll ratios for all reactive/gruparge devices under each gateway
  useEffect(() => {
    if (gateways.length === 0) return;

    async function fetchAllRatios() {
      const penaltyMap = {};

      await Promise.all(
        gateways.map(async (gw) => {
          const reactiveDevices = (gw.devices || []).filter(
            (d) => d.type === "PRC-15H+TCR" || d.type === "Grup Arge Klasik Röle"
          );

          const results = await Promise.all(
            reactiveDevices.map(async (dev) => {
              try {
                const endpoint =
                  dev.type === "Grup Arge Klasik Röle"
                    ? `${API_BASE}/gruparge/ratios?deviceId=${dev.id}`
                    : `${API_BASE}/reactive/ratios?deviceId=${dev.id}`;

                const res = await fetch(endpoint);
                if (!res.ok) return false;
                const json = await res.json();

                const endNum = Number(json?.tanPhiEnd || 0);
                const capNum = Math.abs(Number(json?.tanPhiCap || 0));
                return endNum > 20 || capNum > 15;
              } catch {
                return false;
              }
            })
          );

          penaltyMap[gw.id] = results.some(Boolean);
        })
      );

      setGatewayPenalties(penaltyMap);
    }

    fetchAllRatios();
    const interval = setInterval(fetchAllRatios, 5000);
    return () => clearInterval(interval);
  }, [API_BASE, gateways]);

  async function fetchPenaltyReport() {
    if (!reportStartDate || !reportEndDate) return;
    setReportLoading(true);
    setReportData(null);

    const rows = [];
    await Promise.all(
      gateways.map(async (gw) => {
        const reactiveDevices = (gw.devices || []).filter(
          (d) => d.type === "PRC-15H+TCR" || d.type === "Grup Arge Klasik Röle"
        );
        await Promise.all(
          reactiveDevices.map(async (dev) => {
            try {
              const prefix = dev.type === "Grup Arge Klasik Röle" ? "/gruparge" : "/reactive";
              const url = `${API_BASE}${prefix}/daily-ratios-history?deviceId=${dev.id}&range=${reportRange}&startDate=${reportStartDate}&endDate=${reportEndDate}`;
              const res = await fetch(url);
              if (!res.ok) return;
              const periods = await res.json();
              periods.forEach((p) => {
                rows.push({
                  gatewayName: gw.name,
                  deviceName: dev.name,
                  periodStart: p.periodStart,
                  indRatio: p.indRatio,
                  capRatio: p.capRatio,
                  penalty: p.penalty,
                });
              });
            } catch { /* skip failed devices */ }
          })
        );
      })
    );

    rows.sort((a, b) => {
      const penaltyOrder = { BOTH: 0, INDUCTIVE: 1, CAPACITIVE: 2, NONE: 3 };
      const pa = penaltyOrder[a.penalty] ?? 3;
      const pb = penaltyOrder[b.penalty] ?? 3;
      if (pa !== pb) return pa - pb;
      return a.gatewayName.localeCompare(b.gatewayName);
    });

    setReportData(rows);
    setReportAppliedRange(reportRange);
    setReportLoading(false);
  }

  function formatReportPeriod(periodStart, range) {
    if (!periodStart) return t("ratios.rangeTotalFromFirst") || "Toplam";
    const d = new Date(periodStart);
    if (range === "daily") return d.toLocaleDateString("tr-TR");
    if (range === "weekly") {
      const end = new Date(d);
      end.setDate(end.getDate() + 6);
      return `${d.toLocaleDateString("tr-TR")} – ${end.toLocaleDateString("tr-TR")}`;
    }
    if (range === "monthly") return d.toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
    return d.toLocaleDateString("tr-TR");
  }

  function PenaltyBadge({ penalty }) {
    if (penalty === "INDUCTIVE") return <span className="text-red-600 font-semibold">{t("ratios.penaltyInductive")}</span>;
    if (penalty === "CAPACITIVE") return <span className="text-orange-600 font-semibold">{t("ratios.penaltyCapacitive")}</span>;
    if (penalty === "BOTH") return <span className="text-red-700 font-bold">{t("ratios.penaltyBoth")}</span>;
    return <span className="text-green-600">{t("ratios.noPenalty")}</span>;
  }

  // 🔹 Toplam analizör / reaktif cihaz sayıları
  const analyzerCount = gateways.reduce(
    (sum, gw) =>
      sum +
      (gw.devices || []).filter((d) =>
        ["PAD-01", "PAD-02", "PAD-03"].includes(d.type)
      ).length,
    0
  );

  const reactiveCount = gateways.reduce(
    (sum, gw) =>
      sum +
      (gw.devices || []).filter((d) =>
        ["PRC-15H+TCR", "Grup Arge Klasik Röle"].includes(d.type)
      ).length,
    0
  );

  const multimeterCount = gateways.reduce(
    (sum, gw) =>
      sum +
      (gw.devices || []).filter((d) => d.type === "PMA-03").length,
    0
  );

  const voltampermetreCount = gateways.reduce(
    (sum, gw) =>
      sum +
      (gw.devices || []).filter((d) => d.type === "Voltampermetre").length,
    0
  );

  const penaltyGatewayCount = gateways.filter((gw) => gatewayPenalties[gw.id] === true).length;

  const filteredGateways = gateways.filter((gw) => {
    const hasPenalty = gatewayPenalties[gw.id] === true;
    if (penaltyFilter === "penalty") return hasPenalty;
    if (penaltyFilter === "ok") return !hasPenalty;
    return true;
  });

  return (
    <div className="p-4 md:p-8 min-h-screen bg-gray-50">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{t('gatewayList.title')}</h1>

        <div className="flex items-center gap-2">
          {/* Penalty Filter */}
          {["all", "penalty", "ok"].map((val) => {
            const label =
              val === "all" ? t("ratios.filterAll") :
              val === "penalty" ? t("ratios.filterPenaltyOnly") :
              t("ratios.filterNoPenalty");
            const active = penaltyFilter === val;
            return (
              <button
                key={val}
                onClick={() => setPenaltyFilter(val)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition border ${
                  active
                    ? val === "penalty"
                      ? "bg-red-600 text-white border-red-600"
                      : "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                }`}
              >
                {label}
                {val === "penalty" && penaltyGatewayCount > 0 && (
                  <span className={`ml-1.5 text-xs font-bold px-1.5 py-0.5 rounded-full ${active ? "bg-white text-red-600" : "bg-red-100 text-red-600"}`}>
                    {penaltyGatewayCount}
                  </span>
                )}
              </button>
            );
          })}

          <button
            onClick={() => setReportOpen((v) => !v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border transition ${
              reportOpen
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
            }`}
          >
            <FileBarChart2 className="w-4 h-4" />
            {t("ratios.penaltyReport")}
            {reportOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>
      </div>

      {/* Ceza Raporu Paneli */}
      {reportOpen && (
        <div className="bg-white rounded-xl shadow-md p-5 mb-6 border border-blue-100">
          <h2 className="text-base font-bold text-gray-700 mb-4 flex items-center gap-2">
            <FileBarChart2 className="w-4 h-4 text-blue-600" />
            {t("ratios.penaltyReport")}
          </h2>

          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">{t("ratios.penaltyReportStart")}</label>
              <input
                type="date"
                value={reportStartDate}
                max={reportEndDate}
                onChange={(e) => setReportStartDate(e.target.value)}
                className="border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">{t("ratios.penaltyReportEnd")}</label>
              <input
                type="date"
                value={reportEndDate}
                min={reportStartDate}
                max={today}
                onChange={(e) => setReportEndDate(e.target.value)}
                className="border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">{t("ratios.penaltyReportPeriodLabel")}</label>
              <div className="flex gap-1">
                {[
                  { key: "daily", label: t("ratios.rangeDaily") },
                  { key: "weekly", label: t("ratios.rangeWeekly") },
                  { key: "monthly", label: t("ratios.rangeMonthly") },
                  { key: "total", label: t("ratios.rangeTotal") },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setReportRange(opt.key)}
                    className={`px-2.5 py-1.5 rounded-md text-xs border ${
                      reportRange === opt.key
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={fetchPenaltyReport}
              disabled={reportLoading}
              className="px-4 py-1.5 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {reportLoading ? t("common.loading") : t("ratios.penaltyReportFetch")}
            </button>
          </div>

          {reportData && (
            <>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-gray-500">
                  {reportData.filter((r) => r.penalty !== "NONE").length} {t("ratios.penaltyReportPenalizedCount")}
                  {" / "}
                  {reportData.length} {t("ratios.penaltyReportTotalCount")}
                </p>
                <button
                  onClick={() => setShowAllPeriods((v) => !v)}
                  className="text-xs text-blue-600 hover:underline"
                >
                  {showAllPeriods ? t("ratios.penaltyReportShowOnlyPenalized") : t("ratios.penaltyReportShowAll")}
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-indigo-50 text-gray-700 text-left">
                      <th className="border px-3 py-2">{t("ratios.penaltyReportFacility")}</th>
                      <th className="border px-3 py-2">{t("ratios.penaltyReportDevice")}</th>
                      <th className="border px-3 py-2">{t("ratios.period")}</th>
                      <th className="border px-3 py-2 text-center">{t("ratios.penaltyReportInductive")}</th>
                      <th className="border px-3 py-2 text-center">{t("ratios.penaltyReportCapacitive")}</th>
                      <th className="border px-3 py-2 text-center">{t("ratios.penaltyReportStatus")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData
                      .filter((r) => showAllPeriods || r.penalty !== "NONE")
                      .map((row, i) => (
                        <tr
                          key={i}
                          className={
                            row.penalty === "BOTH" ? "bg-red-100" :
                            row.penalty !== "NONE" ? "bg-red-50" :
                            "hover:bg-gray-50"
                          }
                        >
                          <td className="border px-3 py-2 font-medium">{row.gatewayName}</td>
                          <td className="border px-3 py-2">{row.deviceName}</td>
                          <td className="border px-3 py-2 text-gray-600">{formatReportPeriod(row.periodStart, reportAppliedRange || reportRange)}</td>
                          <td className="border px-3 py-2 text-center text-green-700 font-semibold">
                            %{Number(row.indRatio).toFixed(2)}
                          </td>
                          <td className="border px-3 py-2 text-center text-red-700 font-semibold">
                            %{Number(row.capRatio).toFixed(2)}
                          </td>
                          <td className="border px-3 py-2 text-center">
                            <PenaltyBadge penalty={row.penalty} />
                          </td>
                        </tr>
                      ))}
                    {reportData.filter((r) => showAllPeriods || r.penalty !== "NONE").length === 0 && (
                      <tr>
                        <td colSpan={6} className="border px-3 py-4 text-center text-green-600 font-medium">
                          {t("ratios.penaltyReportNone")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Özet Kartlar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {analyzerCount > 0 && (
          <div className="flex bg-white rounded-xl shadow-md overflow-hidden w-full sm:w-64">
            <div className="bg-indigo-500 flex items-center justify-center w-20">
              <Monitor className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1 text-center py-4">
              <p className="text-2xl font-bold text-gray-800">
                {analyzerCount}
              </p>
              <p className="text-gray-500 text-sm">{t('common.analyzer')}</p>
            </div>
          </div>
        )}

        {reactiveCount > 0 && (
          <div className="flex bg-white rounded-xl shadow-md overflow-hidden w-full sm:w-64">
            <div className="bg-purple-500 flex items-center justify-center w-20">
              <Server className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1 text-center py-4">
              <p className="text-2xl font-bold text-gray-800">
                {reactiveCount}
              </p>
              <p className="text-gray-500 text-sm">{t('common.reactive')}</p>
            </div>
          </div>
        )}
        {multimeterCount > 0 && (
          <div className="flex bg-white rounded-xl shadow-md overflow-hidden w-full sm:w-64">
            <div className="bg-green-500 flex items-center justify-center w-20">
              <Activity className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1 text-center py-4">
              <p className="text-2xl font-bold text-gray-800">{multimeterCount}</p>
              <p className="text-gray-500 text-sm">{t('common.multimeter')}</p>
            </div>
          </div>
        )}
        {voltampermetreCount > 0 && (
          <div className="flex bg-white rounded-xl shadow-md overflow-hidden w-full sm:w-64">
            <div className="bg-yellow-500 flex items-center justify-center w-20">
              <Activity className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1 text-center py-4">
              <p className="text-2xl font-bold text-gray-800">{voltampermetreCount}</p>
              <p className="text-gray-500 text-sm">{t('common.voltampermetre')}</p>
            </div>
          </div>
        )}
      </div>


      {/* Gateway Kartları */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6">
        {filteredGateways.map((gw) => {
          const gwAnalyzerCount = (gw.devices || []).filter((d) =>
            ["PAD-01", "PAD-02", "PAD-03"].includes(d.type)
          ).length;

          const gwReactiveCount = (gw.devices || []).filter((d) =>
            ["PRC-15H+TCR", "Grup Arge Klasik Röle"].includes(d.type)
          ).length;

          const gwMultimeterCount = (gw.devices || []).filter((d) =>
            d.type === "PMA-03"
          ).length;

          const gwVoltampermetreCount = (gw.devices || []).filter((d) =>
            d.type === "Voltampermetre"
          ).length;

          const hasPenalty = gatewayPenalties[gw.id] === true;

          return (
            <div
              key={gw.id}
              className={`rounded-xl shadow-md p-6 hover:shadow-lg transition border-2 ${
                hasPenalty ? "bg-red-50 border-red-300" : "bg-white border-transparent"
              }`}
            >
              <div className="flex items-center space-x-3 mb-3">
                <div className={`p-3 rounded-lg ${hasPenalty ? "bg-red-100 text-red-600" : "bg-indigo-100 text-indigo-600"}`}>
                  <Server className="w-6 h-6" />
                </div>
                <h2 className="text-lg font-semibold text-gray-800">{gw.name}</h2>
                {hasPenalty && (
                  <span className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold bg-red-600 text-white">
                    <AlertTriangle className="w-3 h-3" />
                    {t("ratios.penalty")}
                  </span>
                )}
              </div>

              {gw.description && (
                <p className="text-gray-500 text-sm mb-3">{gw.description}</p>
              )}

              {/* Konum Bilgisi - Tıklanabilir Google Maps Linki */}
              <div className="flex items-center text-xs text-gray-500 mb-3 h-4">
                {gw.latitude && gw.longitude ? (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${gw.latitude},${gw.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center hover:text-indigo-600 transition-colors cursor-pointer"
                    title={t('gatewayList.openInMaps')}
                  >
                    <MapPin className="w-3 h-3 mr-1" />
                    <span className="hover:underline">{gw.latitude}, {gw.longitude}</span>
                  </a>
                ) : null}
              </div>

              {/* Cihaz Sayıları */}
              <div className="text-xs text-gray-500 mb-3 space-y-1 border-t border-b py-2 my-2">
                <div className="flex justify-between">
                  <span>{t('common.reactive')}:</span>
                  <span className="font-medium text-gray-700">{gwReactiveCount}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('common.analyzer')}:</span>
                  <span className="font-medium text-gray-700">{gwAnalyzerCount}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('common.multimeter')}:</span>
                  <span className="font-medium text-gray-700">{gwMultimeterCount}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('common.voltampermetre')}:</span>
                  <span className="font-medium text-gray-700">{gwVoltampermetreCount}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('common.inverter')}:</span>
                  <span className="font-medium text-gray-700">0</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('common.meter')}:</span>
                  <span className="font-medium text-gray-700">0</span>
                </div>
              </div>

              {(() => {
                const lastCommDate = gw.last_comm ? new Date(gw.last_comm) : null;
                const isLate = lastCommDate
                  ? (new Date() - lastCommDate) > 30 * 60 * 1000
                  : true;

                return lastCommDate ? (
                  <p className={`text-xs mb-3 ${isLate ? "text-red-500 font-bold" : "text-gray-400"}`}>
                    {t('common.lastComm')}:{" "}
                    {lastCommDate.toLocaleString("tr-TR", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </p>
                ) : (
                  <p className="text-xs text-red-500 font-bold mb-3">{t('common.lastComm')}: {t('common.none')}</p>
                );
              })()}

              {/* Butonlar */}
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    localStorage.setItem("gatewayId", gw.id);   // 🔥 gatewayId kaydediyoruz
                    navigate(`/gateways/${gw.id}/devices`);
                  }}
                  className="flex-1 text-xs bg-indigo-500 text-white px-3 py-2 rounded-md hover:bg-indigo-600"
                >
                  {t('common.devices')}
                </button>

              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
